import { useModal } from 'modals';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { useSnapshot } from 'valtio';

import * as authStore from '@cartwhl/common/stores/authStore';
import * as userStore from '@cartwhl/common/stores/userStore';
import { getAppletsUrl } from '@cartwhl/common/utils/getAppletsUrl';
import { getStudioUrl } from '@cartwhl/common/utils/getStudioUrl';
import { isExpiredFreeTrial } from '@cartwhl/common/utils/organizationWorkspace';
import Text from '@cartwhl/ui/base/TextNew';
import Button from '@cartwhl/ui/input/Button';

import useHasHydrated from '../../hooks/useHasHydrated';
import NavigationLink from '../NavigationLink';
import StartOrLaunchButton from '../StartOrLaunchButton';
import Wordmark from '../Wordmark';
import { pageFrame } from '../page-frame';
import { publicPageGutters, usesPublicPageGutters } from '../public-page-gutters';
import { type FooterItem, footerGroups, legalLinks } from './content';

interface FooterLinkProps {
  compact?: boolean;
  item: FooterItem;
  appletsUrl: string;
  studioUrl: string;
}

function FooterLink({
  compact = false,
  item,
  appletsUrl,
  studioUrl,
}: FooterLinkProps) {
  const token = compact ? '14/medium' : '16/medium';
  const visibility =
    item.visibility === 'mobile'
      ? 'sm:hidden'
      : item.visibility === 'desktop'
        ? 'hidden sm:block'
        : '';
  const className = `text-gray-65 dark:text-gray-45 ${visibility}`;

  const href =
    'destination' in item
      ? item.destination === 'studio'
        ? studioUrl
        : `${appletsUrl}/mascot`
      : item.href;
  const external = 'destination' in item ? true : item.external;
  return (
    <Text
      as={NavigationLink}
      token={token}
      leading="body"
      href={href}
      external={external}
      className={className}
    >
      {item.label}
    </Text>
  );
}

export default function Footer() {
  const [appletsUrl, setAppletsUrl] = useState(() => getAppletsUrl(false));
  const [studioUrl, setStudioUrl] = useState(() => getStudioUrl(false));
  useEffect(() => {
    setAppletsUrl(getAppletsUrl());
    setStudioUrl(getStudioUrl());
  }, []);
  const { open } = useModal();
  const { authStatus } = useSnapshot(authStore.state);
  const { organizationInfo } = useSnapshot(userStore.state);
  const hasHydrated = useHasHydrated();
  const showStartOrLaunchCta =
    !hasHydrated ||
    authStatus === 'user' ||
    !isExpiredFreeTrial(organizationInfo);
  const { pathname } = useLocation();
  const isPrimaryPricing =
    pathname === '/pricing' || pathname.startsWith('/pricing/');
  const pricingCtaHref =
    pathname === '/pricing/enterprise-plans'
      ? '/pricing#pricing-cards'
      : isPrimaryPricing
        ? `${pathname}#pricing-cards`
        : '/pricing';

  return (
    <footer
      className={`cw-text-box-none bg-primary pb-10 sm:pb-10 xl:pb-20 ${usesPublicPageGutters(pathname) ? publicPageGutters : 'px-4 sm:px-10 2xl:px-12'} ${isPrimaryPricing ? 'border-primary border-t pt-10 md:pt-14 xl:pt-20 min-[1441px]:pt-[100px]' : 'pt-10 sm:pt-20 2xl:pt-24'} ${pageFrame}`}
    >
      <div className="sm:grid sm:grid-cols-[9rem_minmax(0,1fr)] sm:items-start sm:gap-20 lg:grid-cols-[minmax(0,1fr)_28rem] lg:gap-0 xl:grid-cols-[minmax(0,1fr)_794px] 2xl:grid-cols-[minmax(0,1fr)_1007px]">
        <div>
          <div className="flex flex-col">
            <div>
              <Text
                as="div"
                token="16/medium"
                className="text-gray-65 dark:text-gray-45"
              >
                Cartwheel ©{new Date().getFullYear()}
              </Text>
              <Text
                as="address"
                token="16/medium"
                leading="body"
                className="text-gray-65 dark:text-gray-45 mt-6 not-italic"
              >
                Cartwheel Inc.
                <br />
                46 Howard Street
                <br />
                Suite 607
                <br />
                New York, NY 10013
                <br />
                USA
              </Text>
            </div>
            <div className="mt-6 flex gap-4 sm:flex-col sm:items-start lg:flex-row xl:mt-[46px]">
              <Button
                type="button"
                onClick={() => open('ENTERPRISE_CONTACT', {})}
                variant="secondary"
                className="2xl:w-37 flex-1 sm:flex-none"
              >
                Discuss a project
              </Button>
              {showStartOrLaunchCta && (
                <StartOrLaunchButton
                  anonymousHref={pricingCtaHref}
                  className="2xl:w-37 flex-1 sm:flex-none"
                />
              )}
            </div>
            <div className="mt-6 flex gap-5">
              {legalLinks.map((item) => (
                <FooterLink
                  key={item.id}
                  item={item}
                  appletsUrl={appletsUrl}
                  studioUrl={studioUrl}
                  compact
                />
              ))}
            </div>
          </div>
        </div>
        <nav
          aria-label="Footer navigation"
          className="border-primary mt-10 grid grid-cols-2 gap-x-10 gap-y-10 border-t pt-10 sm:mt-0 sm:grid-cols-3 sm:gap-x-6 sm:gap-y-10 sm:border-0 sm:pt-0 xl:flex xl:justify-between xl:gap-0"
        >
          {footerGroups.map((group) => {
            return (
              <section key={group.id} className="min-w-0 xl:shrink-0">
                <Text
                  as="h2"
                  token="16/medium"
                  leading="body"
                  className="text-primary mb-6"
                >
                  {group.label}
                </Text>
                <div className="flex min-w-0 flex-col gap-2">
                  {group.items.map((item) => (
                    <FooterLink
                      key={item.id}
                      item={item}
                      appletsUrl={appletsUrl}
                      studioUrl={studioUrl}
                    />
                  ))}
                </div>
              </section>
            );
          })}
        </nav>
      </div>
      <div className="mt-12 sm:mx-2 sm:mt-10 xl:mt-12 2xl:mx-0">
        <Wordmark decorative large />
      </div>
    </footer>
  );
}
